import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslationService } from '@nebula-shared/data-access';
import { AuthService } from '@nebula/shared-vault/data-access';
import { FooterComponent } from 'nebula-ui-kit';
import { Subscription, filter } from 'rxjs';
import {
  VAULT_FOOTER_COPYRIGHT,
  VAULT_FOOTER_I18N_FALLBACKS,
} from './vault-footer-branding';

type FooterVariant = 'default' | 'login';

@Component({
  selector: 'app-footer-wrapper',
  templateUrl: './footer-wrapper.component.html',
  styleUrls: ['./footer-wrapper.component.scss'],
  standalone: true,
  imports: [FooterComponent],
})
export class FooterWrapperComponent implements OnInit, OnDestroy {
  @Input() variant: FooterVariant = 'default';
  @Input() defaultTenantName = '';
  @Input() displayTenant = true;

  private readonly authService = inject(AuthService);
  private readonly translation = inject(TranslationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly router = inject(Router);

  readonly tenantName = signal('');
  readonly showTenant = signal(true);
  readonly now = signal(new Date());
  isConnected = false;

  private readonly subs = new Subscription();
  private connectionCheckInterval?: ReturnType<typeof setInterval>;

  ngOnInit(): void {
    this.loadTenantFromStorage();
    if (!this.tenantName() && this.defaultTenantName) {
      this.tenantName.set(this.defaultTenantName);
    }
    this.checkCurrentRoute();
    this.checkConnection();
    this.setupPeriodicChecks();

    this.subs.add(
      this.translation.currentLang$.subscribe(() => {
        this.now.set(new Date());
        this.cdr.markForCheck();
      }),
    );

    this.subs.add(
      this.authService.isAuthenticated$.subscribe(() => {
        this.checkConnection();
        this.cdr.markForCheck();
      }),
    );

    this.listenToStorageChanges();
    this.listenToCustomEvents();
    this.listenToRouteChanges();
    this.cdr.markForCheck();
  }

  /** Texto de copyright siempre con marca Vault (la key LAYOUT del bundle es ERP). */
  get copyrightText(): string {
    return VAULT_FOOTER_COPYRIGHT;
  }

  /** Estado de conexión: bundle i18n o fallback Vault. */
  get connectionStatus(): string {
    return this.vaultT(this.isConnected ? 'LAYOUT.CONN_OK' : 'LAYOUT.CONN_FAIL');
  }

  getDisplayTenant(): string {
    return this.tenantName() || this.defaultTenantName || '';
  }

  private vaultT(key: string): string {
    const translated = this.translation.translate(key);
    if (translated && translated !== key) return translated;
    return VAULT_FOOTER_I18N_FALLBACKS[key] ?? key;
  }

  private loadTenantFromStorage(): void {
    if (typeof window === 'undefined') return;

    let tenant = localStorage.getItem('companyName') || '';
    if (!tenant) {
      const userData = localStorage.getItem('user_data');
      if (userData) {
        try {
          const parsed = JSON.parse(userData) as {
            companyName?: string;
            tenant?: string;
          };
          tenant = parsed?.companyName || parsed?.tenant || '';
        } catch {
          tenant = '';
        }
      }
    }

    if (tenant) {
      this.tenantName.set(tenant);
      localStorage.setItem('companyName', tenant);
    } else if (this.defaultTenantName) {
      this.tenantName.set(this.defaultTenantName);
    }
  }

  private checkCurrentRoute(): void {
    const url = this.router.url;
    this.showTenant.set(!(url.includes('/company') || url === '/company'));
  }

  private setupPeriodicChecks(): void {
    this.connectionCheckInterval = setInterval(() => {
      this.checkConnection();
      this.loadTenantFromStorage();
      this.checkCurrentRoute();
      this.cdr.markForCheck();
    }, 30_000);
  }

  private checkConnection(): void {
    if (typeof window === 'undefined') {
      this.isConnected = false;
      return;
    }
    if (this.authService.isAuthenticated()) {
      this.isConnected = true;
      return;
    }
    const userData = localStorage.getItem('user_data');
    this.isConnected = !!userData;
  }

  private listenToStorageChanges(): void {
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', this.handleStorageChange);
    }
  }

  private handleStorageChange = (event: StorageEvent): void => {
    if (event.key === 'user_data' || event.key === 'companyName') {
      this.loadTenantFromStorage();
      this.checkConnection();
      this.cdr.markForCheck();
    }
  };

  private listenToCustomEvents(): void {
    if (typeof window !== 'undefined') {
      window.addEventListener('companySelected', this.handleCompanySelected as EventListener);
      window.addEventListener('tenantSelected', this.handleCompanySelected as EventListener);
    }
  }

  private handleCompanySelected = (event: Event): void => {
    const detail = (event as CustomEvent).detail as
      | { companyName?: string; tenant?: string; name?: string }
      | string
      | undefined;
    if (detail == null) return;

    let tenant = '';
    if (typeof detail === 'string') {
      tenant = detail;
    } else {
      tenant = detail.companyName || detail.tenant || detail.name || '';
    }

    if (tenant) {
      this.tenantName.set(tenant);
      localStorage.setItem('companyName', tenant);
    } else {
      this.loadTenantFromStorage();
    }
    this.checkConnection();
    this.cdr.markForCheck();
  };

  private listenToRouteChanges(): void {
    this.subs.add(
      this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
        this.loadTenantFromStorage();
        this.checkConnection();
        this.checkCurrentRoute();
        this.cdr.markForCheck();
      }),
    );
  }

  ngOnDestroy(): void {
    if (this.connectionCheckInterval) {
      clearInterval(this.connectionCheckInterval);
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', this.handleStorageChange);
      window.removeEventListener('companySelected', this.handleCompanySelected as EventListener);
      window.removeEventListener('tenantSelected', this.handleCompanySelected as EventListener);
    }
    this.subs.unsubscribe();
  }
}
